// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import { formatMeritLadder, formatMerits } from '@/features/game-definitions/merits';
import { t } from '@/i18n';
import appColors from '@/theme/colors';

// One game's share of the tour's merits, as the backend projected it for the chosen weight
// (a MeritLadderRow plus the name to print). Nothing here is multiplied or summed per game.
export type MeritDistributionEntry = {
  gameId: string;
  name: string;
  ladder: number[];
  winnerTotal: number;
  // Null when the game's occurrences cannot be predicted (achievements, hazards): the segment
  // still shows, sized by the single award, but no projected total is printed.
  tourTotal: number | null;
};

type Props = {
  entries: MeritDistributionEntry[];
  // Rounds in the tour; only decides which wording the header uses.
  roundCount?: number;
};

const SEGMENT_COLORS = [appColors.accent.primary, appColors.accent.gold, appColors.accent.soft, appColors.chart.peach, appColors.chart.sky, appColors.status.errorTextSoft];

export default function MeritDistributionBar({ entries, roundCount = 1 }: Props) {
  const withMerits = entries.filter((entry) => entry.ladder.length > 0);
  if (withMerits.length < 2) {
    return null;
  }

  const rounds = Math.max(1, roundCount);
  const totalWinnerMerits = withMerits.reduce((sum, entry) => sum + entry.winnerTotal, 0);
  if (totalWinnerMerits <= 0) {
    return null;
  }
  const segmentSize = (entry: MeritDistributionEntry) => entry.tourTotal ?? entry.winnerTotal;

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <Text style={styles.title}>{t('selectGames.meritDistributionTitle')}</Text>
        <Text style={styles.total}>
          {rounds > 1
            ? t('selectGames.meritDistributionTotalTour', {
                merits: formatMerits(totalWinnerMerits),
                rounds,
              })
            : t('selectGames.meritDistributionTotal', { merits: formatMerits(totalWinnerMerits) })}
        </Text>
      </View>
      <View style={styles.bar}>
        {withMerits.map((entry, index) => (
          <View
            key={entry.gameId}
            style={[
              styles.segment,
              {
                flex: segmentSize(entry) > 0 ? segmentSize(entry) : 0.0001,
                backgroundColor: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
              },
            ]}
          />
        ))}
      </View>
      <View style={styles.legend}>
        {withMerits.map((entry, index) => (
          <View key={entry.gameId} style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: SEGMENT_COLORS[index % SEGMENT_COLORS.length] }]} />
            <Text style={styles.legendName} numberOfLines={1}>
              {entry.name}
            </Text>
            <Text style={styles.legendLadder}>{formatMeritLadder(entry.ladder, '/')}</Text>
            {entry.tourTotal !== null ? (
              <Text style={styles.legendTotal}>{`= ${formatMerits(entry.tourTotal)}`}</Text>
            ) : null}
          </View>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: appColors.background.panel,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 14,
    gap: 8,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
  },
  title: {
    fontSize: 12,
    color: appColors.text.muted,
    fontWeight: '700',
  },
  total: {
    fontSize: 12,
    color: appColors.accent.gold,
    fontWeight: '700',
  },
  bar: {
    flexDirection: 'row',
    height: 10,
    borderRadius: 5,
    overflow: 'hidden',
    gap: 2,
  },
  segment: {
    height: '100%',
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 16,
    rowGap: 4,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  legendName: {
    fontSize: 11,
    color: appColors.text.secondary,
    maxWidth: 140,
  },
  legendLadder: {
    fontSize: 11,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  legendTotal: {
    fontSize: 11,
    fontWeight: '700',
    color: appColors.accent.gold,
  },
});
